import { FC, useEffect, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { IonButton } from '@ionic/react';
import dayjs from 'dayjs';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { IQueryParamDrivenDrawerContentProvider } from '../components/query-param-driver-drawer/IQueryParamDrivenDrawerContentProvider';
import { QueryParamDrivenDrawerHelper } from '../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { QueryParamDrivenDrawerPage } from '../components/CareMapDrawerPage';
import { BlockUntilActiveCaremap } from '../ActiveCareMapProvider';
import { Text } from '../components/Text';
import { CareMapState } from '../caremap/CareMapState';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { ViewNoteWithQueryParamDrawer } from '../notes/view-note/ViewNoteWithQueryParamDrawer';
import { BeeChatViewModel } from '../helper-bee/BeeChatViewModel';
import { CheckinState } from '../journal/model/CheckinState';
import { UserState } from '../user/UserState';
import { ChevronRightIcon } from '../theme/Icons';
import { CaremapInsightsModelFactory } from './CaremapInsightsModelFactory';
import { InsightsModel } from './InsightsModel';
import { IInsight, IInsightEvidence } from './IInsight';
import { evidenceTarget } from './evidenceTarget';
import { insightTypeMeta } from './insightTypeMeta';
import { evidenceKindMeta, evidenceLockedReason, evidenceDisplaySnippet } from './insightEvidenceMeta';
import { InsightFeedback } from './InsightFeedback';

interface IViewInsightDrawerProps {
    insightId: string;
    surface?: string;
}

export class ViewInsightWithQueryParamDrawer implements IQueryParamDrivenDrawerContentProvider<IViewInsightDrawerProps> {
    static inject = () => [QueryParamDrivenDrawerHelper];
    constructor(private drawerHelper: QueryParamDrivenDrawerHelper<IViewInsightDrawerProps>){}

    mode = 'view-insight';

    Component = ViewInsightDrawerView;

    options = {
        allowBackdropClose: true
    };

    open = (props: IViewInsightDrawerProps) => {
        this.drawerHelper.open({ mode: this.mode, ...props });
    };

    getUrl = (props: IViewInsightDrawerProps) => {
        return this.drawerHelper.getUrl({ mode: this.mode, ...props });
    };
};

const formatDate = (date: string) => dayjs(date).format('MM/DD/YY');

const useStyles = createUseStyles({
    title: {
        fontSize: 20,
        fontWeight: 700,
        color: 'var(--ion-text-color)',
        marginBottom: 8,
    },

    narrative: {
        fontSize: 15,
        lineHeight: '1.6',
        color: 'var(--ion-color-step-900)',
        marginBottom: 14,
    },

    metaRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 20,
        flexWrap: 'wrap' as const,
    },

    confidencePill: {
        display: 'inline-block',
        fontSize: 12,
        fontWeight: 500,
        padding: '2px 10px',
        borderRadius: 999,
        background: 'rgba(var(--ion-color-primary-rgb), 0.15)',
        color: 'var(--ion-color-primary)',
    },

    dateRange: {
        fontSize: 13,
        color: 'var(--ion-color-step-600)',
    },

    // tinted type pill: icon + small-caps label in the type color, on a 10%-opacity wash of it
    typeChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.04em',
        borderRadius: 999,
        padding: '3px 9px',
        marginBottom: 8,
    },

    evidenceSection: {
        marginBottom: 20,
    },

    evidenceSub: {
        marginBottom: 6,
    },

    evidenceRow: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: 11,
        padding: '11px 2px',
        borderBottom: '1px solid var(--ion-color-step-100)',
    },

    evidenceRowTappable: {
        cursor: 'pointer',
    },

    evidenceIcon: {
        flexShrink: 0,
        marginTop: 2,
    },

    evidenceBody: {
        flex: 1,
        minWidth: 0,
    },

    evidenceKind: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: 7,
        fontSize: 11.5,
        fontWeight: 600,
        textTransform: 'uppercase' as const,
        letterSpacing: '0.05em',
        color: 'var(--ion-color-step-600)',
        marginBottom: 3,
    },

    evidenceKindDate: {
        fontWeight: 500,
        textTransform: 'none' as const,
        letterSpacing: 0,
        color: 'var(--ion-color-step-500)',
    },

    evidenceText: {
        fontSize: 14,
        lineHeight: 1.45,
        color: 'var(--ion-text-color)',
    },

    evidenceTextLocked: {
        color: 'var(--ion-color-step-500)',
    },

    evidenceWhy: {
        fontSize: 12,
        fontStyle: 'italic' as const,
        color: 'var(--ion-color-step-500)',
        marginTop: 3,
    },

    evidenceChev: {
        flexShrink: 0,
        alignSelf: 'center',
    },

    actionsBar: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: 8,

        '& ion-button': {
            flex: '1 1 auto',
        },
    },

    emptyState: {
        padding: 20,
    },
});

const InsightEvidenceRow: FC<{ evidence: IInsightEvidence }> = ({ evidence }) => {
    const classes = useStyles();

    const viewNoteDrawer = useInstance(ViewNoteWithQueryParamDrawer);
    const history = useInstance(HistoryViewModel);
    const careMapState = useInstance(CareMapState);
    const checkinState = useInstance(CheckinState);
    const userState = useInstance(UserState);

    const target = evidenceTarget({
        evidence,
        currentUserId: userState.state.userId,
        // the drawer renders inside BlockUntilActiveCaremap, so this is the ACTIVE caremap's owner
        // (getActiveCaremap's return type doesn't expose userId, hence the mostRecent accessor)
        careMapOwnerUserId: careMapState.getMostRecentCaremap()?.userId
    });
    const isTappable = target.kind !== 'none';
    const { label, Icon } = evidenceKindMeta(evidence.ref);
    const lockedReason = isTappable ? undefined : evidenceLockedReason(evidence.ref);

    const onClick = () => {
        if (target.kind === 'note') {
            viewNoteDrawer.open({ noteId: target.noteId });
        } else if (target.kind === 'checkin') {
            // opens EditCheckinModal, which is mounted globally (InnerhiveApp.tsx) rather than
            // scoped to the journal page -- same pattern JournalPage's calendar day-click uses
            checkinState.startEditing({ date: target.date, checkinType: target.checkinType, trackingFrom: 'Insight Evidence' });
        } else if (target.kind === 'appointment') {
            history.push('/calendar/appointments');
        } else if (target.kind === 'task') {
            const careMapId = careMapState.getActiveCaremap()?.careMapId;
            if (careMapId) {
                history.push(`/calendar/tasks/${careMapId}?highlightTaskId=${target.taskId}`);
            }
        }
    };

    return (
        <div
            className={`${classes.evidenceRow} ${isTappable ? classes.evidenceRowTappable : ''}`}
            data-type="insight-evidence-row"
            onClick={isTappable ? onClick : undefined}
        >
            <span className={classes.evidenceIcon}>
                <Icon color={isTappable ? 'primary' : 'medium'} size="small" />
            </span>
            <div className={classes.evidenceBody}>
                <div className={classes.evidenceKind}>
                    <span>{label}</span>
                    <span className={classes.evidenceKindDate}>{formatDate(evidence.date)}</span>
                </div>
                <div className={`${classes.evidenceText} ${isTappable ? '' : classes.evidenceTextLocked} mp-mask`}>
                    {evidenceDisplaySnippet(evidence.snippet)}
                </div>
                {lockedReason && <div className={classes.evidenceWhy}>{lockedReason}</div>}
            </div>
            {isTappable && (
                <span className={classes.evidenceChev}>
                    <ChevronRightIcon color="medium" size="small" />
                </span>
            )}
        </div>
    );
};

const InsightDetail: FC<{
    insight: IInsight;
    model: InsightsModel;
    onChat: () => void;
    surface: string;
    onArchived: () => void;
}> = ({ insight, model, onChat, surface, onArchived }) => {
    const classes = useStyles();

    const { label: typeLabel, Icon: TypeIcon, color: typeColor } = insightTypeMeta(insight.type);
    const itemCount = insight.evidence.length;

    return (
        <div data-id="insight-drawer" data-type="insight-drawer-content" data-insight-id={insight.insightId}>
            <div className={classes.typeChip} style={{ color: typeColor, backgroundColor: `${typeColor}1A` }}>
                <TypeIcon color="inherit" size="small" />
                {typeLabel}
            </div>
            <div className={`${classes.title} mp-mask`}>{insight.title}</div>
            <div className={`${classes.narrative} mp-mask`}>{insight.narrative}</div>

            <div className={classes.metaRow}>
                <span className={classes.confidencePill}>{insight.confidence.charAt(0).toUpperCase() + insight.confidence.slice(1)} confidence</span>
                <span className={classes.dateRange}>
                    {formatDate(insight.dateRangeFrom)} – {formatDate(insight.dateRangeTo)}
                </span>
            </div>

            {itemCount > 0 && (
                <div className={classes.evidenceSection}>
                    <Text type="subtitle">What this is based on</Text>
                    <div className={classes.evidenceSub}>
                        <Text type="detail">{itemCount} item{itemCount === 1 ? '' : 's'}</Text>
                    </div>
                    {insight.evidence.map((evidence, index) => (
                        <InsightEvidenceRow key={`${evidence.ref}-${index}`} evidence={evidence} />
                    ))}
                </div>
            )}

            <div className={classes.actionsBar}>
                <IonButton
                    color="primary"
                    fill="outline"
                    onClick={onChat}
                    data-id="insight-chat-button"
                >
                    Chat about this
                </IonButton>
            </div>

            {/* An archived insight is read-only: re-offering the thumbs would let a stray tap
                overwrite the stored not-useful reason the archive displays. */}
            {!insight.isDismissed && (
                <InsightFeedback insight={insight} model={model} surface={surface} onArchived={onArchived} />
            )}
        </div>
    );
};

// Fires once per mount the moment the insight becomes available: on a direct/deep-link open the
// model may still be loading on first render, so `insight` can go from undefined to defined on a
// later render -- the ref guards against re-firing "Insight Card Viewed" on that later render (or
// any other re-render) once it already has.
const useInsightSeenTracking = (params: {
    model: InsightsModel;
    insight: IInsight | undefined;
    surface?: string;
    tracking: MixPanelEventEmitter;
}) => {
    const { model, insight, surface, tracking } = params;
    const hasTrackedViewRef = useRef(false);

    useEffect(() => {
        if (!insight || hasTrackedViewRef.current) {
            return;
        }
        hasTrackedViewRef.current = true;

        model.markSeen(insight);
        tracking.track('Insight Card Viewed', () => ({
            'Type': insight.type,
            'Confidence': insight.confidence,
            'Surface': surface ?? 'home'
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [insight]);
};

const ViewInsightDrawerContent: FC<IViewInsightDrawerProps> = ({ insightId, surface }) => {
    const classes = useStyles();

    const factory = useInstance(CaremapInsightsModelFactory);
    useSubscription(() => factory);

    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);
    const tracking = useInstance(MixPanelEventEmitter);
    const beeChatViewModel = useInstance(BeeChatViewModel);

    const model = factory.forActiveCaremap();
    // getInsights (not getActive) so rows in the "Not useful" archive can still open here --
    // getActive filters out dismissed insights, which is exactly what the archive holds.
    const insight = model.getInsights().find(candidate => candidate.insightId === insightId);

    useInsightSeenTracking({ model, insight, surface, tracking });

    // Guard on hasLoaded so a fresh deep-link (insights not fetched yet, so `insight` is
    // legitimately undefined) doesn't flash the "no longer available" state before the fetch has
    // a chance to resolve (mirrors ViewNoteUsingCareMapDrawer's hasLoaded guard).
    if (!model.hasLoaded) {
        return null;
    }

    if (!insight) {
        return (
            <div className={classes.emptyState} data-id="insight-drawer" data-type="insight-drawer-empty">
                <Text>This pattern is no longer available. It may have been dismissed.</Text>
            </div>
        );
    }

    const onChat = () => {
        beeChatViewModel.open({
            trackingFrom: 'Insight',
            initialMessage: `Tell me more about the pattern you noticed: ${insight.title}`
        });
        tracking.track('Insight Chat Started', () => ({
            'Type': insight.type,
            'Confidence': insight.confidence
        }));
    };

    return (
        <InsightDetail
            insight={insight}
            model={model}
            onChat={onChat}
            // pass the drawer's own surface so 'Insight Feedback' and 'Insight Dismissed' still
            // distinguish a 👍/👎 here from one on the Insights page; closing on archive mirrors
            // how the old Dismiss button behaved.
            surface={surface ?? 'home'}
            onArchived={drawerHelper.close}
        />
    );
};

export const ViewInsightDrawerView: FC<IViewInsightDrawerProps> = () => {
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);
    const { insightId, surface } = drawerHelper.getDrawerParams();

    return (
        <QueryParamDrivenDrawerPage
            title="Insight"
            hasBack={false}
            data-type="view-insight-drawer"
            onClose={drawerHelper.close}
        >
            <BlockUntilActiveCaremap>
                <ViewInsightDrawerContent insightId={insightId} surface={surface} />
            </BlockUntilActiveCaremap>
        </QueryParamDrivenDrawerPage>
    );
};
